import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", structure_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) nav("/espace"); else setError(r.error || "Erreur");
  };

  return (
    <div className="container-x py-16 grid lg:grid-cols-2 gap-14 items-center min-h-[70vh]">
      <div className="hidden lg:block">
        <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-500 text-white shadow-sm mb-6"><Sparkles /></div>
        <h1 className="font-display text-4xl xl:text-5xl font-bold text-slate-900 tracking-tight">Rejoignez la communauté Beehive.</h1>
        <p className="mt-4 text-slate-600 leading-relaxed text-lg max-w-md">Créez gratuitement votre profil membre et accédez à nos contenus, conseils et ressources dédiés aux professionnels de la petite enfance.</p>
        <ul className="mt-6 space-y-3 text-slate-700">
          {[
            "Accédez aux articles et conseils des experts",
            "Suivez les contenus qui vous intéressent",
            "Découvrez les formations Beehive et recevez un devis sous 48h",
            "Faites évoluer votre profil au fil du temps",
            "Téléchargez vos attestations de participation en fin de session",
          ].map((t, i) => (
            <li key={t} className="flex gap-3 text-sm leading-relaxed" data-testid={`profile-benefit-${i}`}>
              <CheckCircle2 size={17} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />{t}
            </li>
          ))}
        </ul>
      </div>
      <div className="max-w-md w-full mx-auto">
        <div className="card-soft p-8" data-testid="register-form">
          <h2 className="font-display font-bold text-2xl text-slate-900">Créer un compte</h2>
          <p className="text-sm text-slate-500 mt-1">Gratuit — accès immédiat à votre espace.</p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <Input data-testid="reg-name" required placeholder="Prénom et nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="reg-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="reg-password" type="password" required minLength={6} placeholder="Mot de passe (6+ caractères)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="reg-role"><SelectValue placeholder="Votre profil" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Assistante maternelle / EJE / Auxiliaire</SelectItem>
                <SelectItem value="educator">Éducateur / Enseignant</SelectItem>
                <SelectItem value="structure_manager">Directeur/trice de structure</SelectItem>
              </SelectContent>
            </Select>
            {form.role === "structure_manager" && (
              <Input data-testid="reg-structure" placeholder="Nom de la structure" value={form.structure_name} onChange={(e) => setForm({ ...form, structure_name: e.target.value })} />
            )}
            {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
            <button data-testid="reg-submit" disabled={loading} className="btn-primary w-full disabled:opacity-70">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Créer mon compte"}
            </button>
          </form>
          <p className="text-sm text-slate-600 text-center mt-6">Déjà un compte ? <Link to="/connexion" className="link-inline">Se connecter</Link></p>
        </div>
      </div>
    </div>
  );
}
